import { Image } from '@components/products/products'
import { API_URL } from '@constants'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Select, { SelectInstance } from 'react-select'
import 'moment/locale/fr'
import { ArticleC } from '@components/article/article'
import { replaceImageExtension } from '@utils'
import { usePageLoading } from '../../context/PageLoadingContext'
import {
    ariaLiveFr,
    createSelectKeyDownHandler,
    screenReaderStatusFr,
} from './selectA11yFr'

interface Article {
    title: string
    image: Image
    description: string
    creationDate: string
    blog_detail: {
        data: {
            id: number
        }
    }
}

interface IArticles {
    id: number
    attributes: Article
}

interface Page {
    viewMoreText: string
    knowMoreText: string
    title: string
}
interface Categorie {
    label: string
}
interface ICategorie {
    id: number
    attributes: Categorie
}

export const BlogContent: React.FC = () => {
    const datesOption = [
        { value: 'asc', label: 'Ordre Croissant' },
        { value: 'desc', label: 'Ordre Décroissant' },
    ]

    const categoriesOption: ICategorie[] = [
        {
            id: 0,
            attributes: {
                label: 'Tous',
            },
        },
    ]
    const start: number = 0
    const initialLimit = 6
    const [limit, setLimit] = useState<number>(initialLimit)
    const pageSize: number = 6
    const [articles, setArticles] = useState<IArticles[]>()
    const [page, setPage] = useState<Page>()
    const [categories, setCategories] = useState<ICategorie[]>()
    const [loading, setLoading] = useState<boolean>(false)
    const [loadMore, setLoadMore] = useState<boolean>(true)
    const [noMore, setNoMore] = useState<boolean>(false)
    const [filterDate, setFilterDate] = useState<string>('desc')
    const [filterCategorie, setFilterCategorie] = useState<number>(0)
    const { incrementLoadingRequestCount, decrementLoadingRequestCount } =
        usePageLoading()
    const [initalLoadArticles, setInitialLoadArticles] = useState(true)
    const [initalLoadBlogPage, setInitialLoadBlogPage] = useState(true)
    const [initalLoadCategories, setInitialLoadCategories] = useState(true)
    const pendingFocusArticleIndex = useRef<number | null>(null)
    const [loadMoreAnnouncement, setLoadMoreAnnouncement] = useState('')

    type DateOption = (typeof datesOption)[number]
    const dateSelectRef = useRef<SelectInstance<DateOption> | null>(null)
    const categorySelectRef = useRef<SelectInstance<ICategorie> | null>(null)

    const selectFocusControl = ({ isFocused }: { isFocused: boolean }) =>
        `flex border-[1px] border-color-[#6C737E]  w-auto min-h-[2.75rem] items-center text-left pl-4 font-semibold text-base rounded-full p-2 overflow-visible ${
            isFocused ? 'border-[#0a8335] rounded-[8rem]' : 'border-color'
        }`
    const selectFocusOption = ({ isFocused }: { isFocused: boolean }) =>
        `w-max px-3 py-2 rounded-xl shadow-none cursor-pointer border-2 ${
            isFocused ? 'border-[#058943]' : 'border-transparent'
        }`

    async function fetchData() {
        let filterCategorieSyntax: string =
            filterCategorie === 0
                ? ''
                : `&filters[categories][id][$eq]=` + filterCategorie
        if (initalLoadArticles) {
            incrementLoadingRequestCount()
        }
        await axiosReadInstance
            .get(
                `${API_URL}/api/blogs?sort=creationDate:` +
                    filterDate +
                    filterCategorieSyntax +
                    `&pagination[start]=` +
                    start +
                    `&pagination[limit]=` +
                    limit +
                    `&populate=*`
            )
            .then((response) => {
                if (limit >= response.data.meta.pagination.total) {
                    setNoMore(true)
                } else {
                    setNoMore(false)
                }
                setArticles(response.data.data)
            })
            .finally(() => {
                if (initalLoadArticles) {
                    decrementLoadingRequestCount()
                    setInitialLoadArticles(false)
                }
            })
    }
    async function fetchPage() {
        if (initalLoadBlogPage) {
            incrementLoadingRequestCount()
        }
        await axiosReadInstance
            .get(`${API_URL}/api/blog-page`)
            .then((response) => setPage(response.data.data.attributes))
            .finally(() => {
                if (initalLoadBlogPage) {
                    decrementLoadingRequestCount()
                    setInitialLoadBlogPage(false)
                }
            })
    }
    async function fetchCategories() {
        if (initalLoadCategories) {
            incrementLoadingRequestCount()
        }
        await axiosReadInstance
            .get(`${API_URL}/api/categories`)
            .then((response) => {
                setCategories([...categoriesOption, ...response.data.data])
            })
            .finally(() => {
                if (initalLoadCategories) {
                    decrementLoadingRequestCount()
                    setInitialLoadCategories(false)
                }
            })
    }

    useEffect(() => {
        setLoadMore(true)
        moment.locale('fr')
        fetchPage()
        fetchCategories()
        fetchData().then(() => {
            setLoadMore(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit])

    useEffect(() => {
        pendingFocusArticleIndex.current = null
        setLoading(true)
        fetchPage()
        setLimit(initialLimit)
        fetchData().then(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDate, filterCategorie])

    useEffect(() => {
        if (loading || !articles?.length) return
        const idx = pendingFocusArticleIndex.current
        if (idx === null) return
        pendingFocusArticleIndex.current = null
        const added = articles.length - idx
        if (added <= 0) return
        const count = Math.min(pageSize, added)
        setLoadMoreAnnouncement(
            `${count} article${count > 1 ? 's' : ''} supplémentaires chargés`
        )
        const t = window.setTimeout(() => {
            document.getElementById(`blog-article-link-${idx}`)?.focus()
        }, 120)
        return () => window.clearTimeout(t)
    }, [articles, loading, pageSize])

    return (
        <section className="bg-white">
            {categories && categories.length > 0 && (
                <div className="max-w-screen-2xl pt-4 pb-8 px-4 md:px-[6rem] mx-auto lg:pt-8 lg:pb-16 lg:px-[8rem]">
                    <p
                        className="sr-only"
                        aria-live="assertive"
                        aria-atomic="true"
                    >
                        {loadMoreAnnouncement}
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 md:gap-4 lg:gap-5 mx-auto">
                        <div className="flex flex-col gap-1 w-48 overflow-visible z-10">
                            <label
                                htmlFor="blog-filter-date-input"
                                className="text-sm font-semibold text-left pl-1"
                            >
                                Date
                            </label>
                            <Select
                                ref={dateSelectRef}
                                inputId="blog-filter-date-input"
                                isSearchable={false}
                                tabSelectsValue={false}
                                aria-label="Trier par date"
                                ariaLiveMessages={ariaLiveFr}
                                screenReaderStatus={screenReaderStatusFr}
                                onKeyDown={createSelectKeyDownHandler(
                                    dateSelectRef
                                )}
                                value={
                                    datesOption.find(
                                        (o) => o.value === filterDate
                                    ) ?? datesOption[1]
                                }
                                onChange={(selected) =>
                                    setFilterDate(selected?.value ?? 'desc')
                                }
                                className="w-full"
                                unstyled
                                menuPortalTarget={
                                    typeof document !== 'undefined'
                                        ? document.body
                                        : null
                                }
                                menuPlacement="auto"
                                classNames={{
                                    control: selectFocusControl,
                                    menu: () =>
                                        'text-left w-max shadow-lg drop-shadow-lg z-[100] overflow-hidden mt-1',
                                    menuList: () => 'p-1',
                                    option: selectFocusOption,
                                }}
                                styles={{
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        color: provided.color,
                                        border: state.isFocused
                                            ? '2px solid #058943'
                                            : '2px solid transparent',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '0.5rem',
                                        fontWeight: '500',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        zIndex: 9999,
                                        border: '1px solid #6C737E',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                options={datesOption}
                            />
                        </div>
                        <div className="flex flex-col gap-1 w-48 overflow-visible z-10">
                            <label
                                htmlFor="blog-filter-category-input"
                                className="text-sm font-semibold text-left pl-1"
                            >
                                Catégories
                            </label>
                            <Select
                                ref={categorySelectRef}
                                inputId="blog-filter-category-input"
                                isSearchable={false}
                                tabSelectsValue={false}
                                aria-label="Filtrer par catégorie"
                                ariaLiveMessages={ariaLiveFr}
                                screenReaderStatus={screenReaderStatusFr}
                                onKeyDown={createSelectKeyDownHandler(
                                    categorySelectRef
                                )}
                                value={
                                    categories.find(
                                        (c) => c.id === filterCategorie
                                    ) ?? null
                                }
                                onChange={(selected) =>
                                    setFilterCategorie(selected?.id ?? 0)
                                }
                                className="w-full"
                                unstyled
                                placeholder="Catégories"
                                menuPortalTarget={
                                    typeof document !== 'undefined'
                                        ? document.body
                                        : null
                                }
                                menuPlacement="auto"
                                classNames={{
                                    control: selectFocusControl,
                                    menu: () =>
                                        'text-left w-max shadow-lg drop-shadow-lg z-[100] overflow-hidden mt-1',
                                    menuList: () => 'p-1',
                                    option: selectFocusOption,
                                }}
                                styles={{
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        color: provided.color,
                                        border: state.isFocused
                                            ? '2px solid #058943'
                                            : '2px solid transparent',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '0.5rem',
                                        fontWeight: '500',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        zIndex: 9999,
                                        border: '1px solid #6C737E',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                options={categories}
                                getOptionLabel={(option) =>
                                    `${option.attributes.label}`
                                }
                                getOptionValue={(option) => `${option.id}`}
                            />
                        </div>
                    </div>

                    {!loading && (
                        <div
                            className={`mt-20 grid grid-cols-1 md:grid-cols-3 ${
                                window.location.pathname !== '/newsroom' ||
                                window.innerWidth > 2000
                                    ? 'md:grid-cols-3'
                                    : ''
                            } gap-4 lg:gap-6 xl:gap-8`}
                        >
                            {articles?.map((art, index) => {
                                const url = `/blog-detail/${art.attributes.blog_detail.data.id}`

                                return (
                                    <ArticleC
                                        url={url}
                                        title={art.attributes.title}
                                        subtitle={moment(
                                            art.attributes.creationDate
                                        ).format('LL')}
                                        description={art.attributes.description}
                                        srcImg={replaceImageExtension(
                                            art.attributes.image.data.attributes
                                                .url
                                        )}
                                        altImg=""
                                        knowMoreText={
                                            page?.knowMoreText
                                                ? page.knowMoreText
                                                : ''
                                        }
                                        key={index}
                                        linkId={`blog-article-link-${index}`}
                                    />
                                )
                            })}
                        </div>
                    )}
                    {!loading && loadMore && (
                        <div className="flex justify-center mt-10">
                            <svg
                                aria-hidden="true"
                                className="inline w-12 h-12 mr-2 animate-spin"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="#e5e7eb"
                                />
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="#22c55e"
                                />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    )}

                    <div className="flex justify-center">
                        {!loading && !loadMore && !noMore && (
                            <button
                                type="button"
                                className="button-secondary px-10 py-1 mt-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2"
                                aria-label="voir plusse"
                                onClick={() => {
                                    pendingFocusArticleIndex.current = limit
                                    setLimit(limit + pageSize)
                                }}
                            >
                                Voir plus
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    )
}
